import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config, EditorField, NumberConfig } from '../@types/editor'

import {
  getSelectedNumbers,
  isFieldComplete as isFieldCompleteBase,
  isPlayComplete as isPlayCompleteHelper,
  setFieldSelected,
} from '../utils/editorFields'
import { editorStorage } from '../utils/editorPersistence'
import {
  createPlayKey,
  hasDeletablePlays,
  pickNumbers,
  pickRandomEntry,
  validatePlay,
} from '../utils/editorPlay'
import { createLotteryStoreCache, createPlaysState } from '../utils/editorStore'

export type EditorPlay = {
  fields: EditorField[]
  id: string
  quickPick?: string
  key: string
  perPlayAddOn: Maybe<{ name: string; value: string }>
  stake: Maybe<string>
}

export type QuickPickSettings = {
  betType: Maybe<string>
  numbers: Maybe<Maybe<number>[]>
  quantity: number
  stake: Maybe<string>
}

const DEFAULT_QUICK_PICK_SETTINGS: QuickPickSettings = {
  betType: null,
  numbers: null,
  quantity: 5,
  stake: null,
}

const QUICK_PICK_ATTEMPTS = 20

const createPlay = (config: Config, index: number): EditorPlay => ({
  fields: config.numbers.map(({ name }) => ({ name, selected: [] })),
  id: `play-${index + 1}`,
  key: createPlayKey(),
  perPlayAddOn: getDefaultPerPlayAddon(config),
  stake: getDefaultStake(config),
})

const getDefaultPerPlayAddon = (config: Config): EditorPlay['perPlayAddOn'] => {
  const perPlayAddOn = getPerPlayAddOnConfig(config)
  if (!perPlayAddOn) return null
  const [firstOption] = [...perPlayAddOn.options]
  return { name: perPlayAddOn.name, value: firstOption ?? '' }
}

const getDefaultStake = (config: Config): EditorPlay['stake'] => config.stakes[0]?.price ?? null

const getPerPlayAddOnConfig = (config: Config) => config.addOns.find(({ perPlay }) => perPlay)

const isNumberSelectable = (config: Config, fieldIndex: number, value: Maybe<number>) => {
  const numberConfig = config.numbers.at(fieldIndex)
  if (typeof value !== 'number' || !numberConfig) return false
  return numberConfig.selectableNumbers.includes(value)
}

const isFieldIgnoredForAddOn = (
  numberConfig: NumberConfig | undefined,
  addOnValue: Maybe<string>,
) => !!addOnValue && !!numberConfig?.ignoredForAddOns.some(({ value }) => value === addOnValue)

const isFieldIgnored = (config: Config, play: EditorPlay, fieldIndex: number) =>
  isFieldIgnoredForAddOn(config.numbers[fieldIndex], play.perPlayAddOn?.value ?? null)

const clearIgnoredFields = (config: Config, play: EditorPlay) =>
  play.fields.forEach((field, fieldIndex) => {
    if (isFieldIgnored(config, play, fieldIndex)) field.selected = []
  })

const getDisabledFieldNames = (config: Config, addOnValue: Maybe<string>): string[] =>
  config.numbers
    .filter((numberConfig) => isFieldIgnoredForAddOn(numberConfig, addOnValue))
    .map(({ name }) => name)

const getIgnoredFieldNames = (config: Config, play: EditorPlay): string[] =>
  getDisabledFieldNames(config, play.perPlayAddOn?.value ?? null)

const isFieldComplete = (config: Config, play: EditorPlay, fieldIndex: number) =>
  isFieldIgnored(config, play, fieldIndex) || isFieldCompleteBase(config, play, fieldIndex)

const isPlayComplete = (config: Config, play: EditorPlay) =>
  isPlayCompleteHelper(config, play, isFieldComplete)

const applySettingsToPlay = (config: Config, play: EditorPlay, settings: QuickPickSettings) => {
  const perPlayAddOn = getPerPlayAddOnConfig(config)
  const betType = settings.betType ?? pickRandomEntry([...(perPlayAddOn?.options ?? [])])

  play.perPlayAddOn =
    perPlayAddOn && betType
      ? { name: perPlayAddOn.name, value: betType }
      : getDefaultPerPlayAddon(config)
  play.stake =
    settings.stake ??
    pickRandomEntry(config.stakes.map(({ price }) => price)) ??
    getDefaultStake(config)

  play.fields.forEach((field, fieldIndex) => {
    if (isFieldIgnored(config, play, fieldIndex)) {
      field.selected = []
      return
    }

    const chosenNumber = settings.numbers?.[fieldIndex]
    field.selected =
      typeof chosenNumber === 'number'
        ? [chosenNumber]
        : pickNumbers(config.numbers[fieldIndex], field.selected)
  })
}

const isPlayAllowed = (lotteryId: LotteryId, config: Config, play: EditorPlay) =>
  validatePlay(lotteryId, config, play.perPlayAddOn?.value ?? null, getSelectedNumbers(play.fields))

// rolls again like the settings quick pick, so an invalid play is never refilled invalid; when the
// kept numbers allow no valid fill, it replaces them too
const quickPickPlay = (lotteryId: LotteryId, config: Config, play: EditorPlay) => {
  const kept = isPlayComplete(config, play) ? [] : play.fields.map(({ selected }) => selected)
  for (const base of [kept, []]) {
    for (let attempt = 0; attempt < QUICK_PICK_ATTEMPTS; attempt++) {
      play.fields.forEach((field, fieldIndex) => {
        if (isFieldIgnored(config, play, fieldIndex)) return
        field.selected = pickNumbers(config.numbers[fieldIndex], base[fieldIndex] ?? [])
      })
      if (isPlayAllowed(lotteryId, config, play)) return
    }
  }
}

const isDeterministic = (config: Config, settings: QuickPickSettings) =>
  !!settings.betType &&
  config.numbers.every(
    (numberConfig, fieldIndex) =>
      isFieldIgnoredForAddOn(numberConfig, settings.betType) ||
      typeof settings.numbers?.[fieldIndex] === 'number',
  )

const quickPickPlayWithSettings = (
  lotteryId: LotteryId,
  config: Config,
  play: EditorPlay,
  settings: QuickPickSettings,
): boolean => {
  const selectedBefore = play.fields.map(({ selected }) => [...selected])
  const perPlayAddOnBefore = play.perPlayAddOn
  const stakeBefore = play.stake

  const restore = () => {
    play.fields.forEach(
      (field, fieldIndex) => (field.selected = [...(selectedBefore[fieldIndex] ?? [])]),
    )
    play.perPlayAddOn = perPlayAddOnBefore
    play.stake = stakeBefore
  }

  const attempts = isDeterministic(config, settings) ? 1 : QUICK_PICK_ATTEMPTS

  for (let attempt = 0; attempt < attempts; attempt++) {
    restore()
    applySettingsToPlay(config, play, settings)

    if (isPlayAllowed(lotteryId, config, play)) return true
  }

  restore()
  return false
}

const reconcilePlay = (config: Config, play: EditorPlay) => {
  const perPlayAddOn = getPerPlayAddOnConfig(config)
  const isAddOnValid =
    !!perPlayAddOn &&
    play.perPlayAddOn?.name === perPlayAddOn.name &&
    perPlayAddOn.options.includes(play.perPlayAddOn.value)

  if (!isAddOnValid) play.perPlayAddOn = getDefaultPerPlayAddon(config)
  if (!config.stakes.some(({ price }) => price === play.stake)) play.stake = getDefaultStake(config)

  clearIgnoredFields(config, play)
}

const createStore = (lotteryId: LotteryId) =>
  defineStore(
    `ltc_pick_lottery_${lotteryId.toLowerCase()}`,
    () => {
      const playsState = createPlaysState({
        createPlay,
        isPlayComplete,
        isPlayValid: (config, play) => isPlayAllowed(lotteryId, config, play),
        lotteryId,
        quickPickPlay: (config, play) => quickPickPlay(lotteryId, config, play),
        reconcilePlay,
      })
      const {
        getPlay,
        plays,
        quickPickPlays,
        removeEmptyPlaysExcept,
        setUp: setUpPlays,
      } = playsState

      const quickPickSettings = ref<QuickPickSettings>({ ...DEFAULT_QUICK_PICK_SETTINGS })

      const canDeleteAllPlays = (config: Config) =>
        hasDeletablePlays(plays.value, (play) => isPlayComplete(config, play))

      const clearPlay = (playId: string, config: Config) => {
        const play = getPlay(playId)
        if (!play) return
        play.fields.forEach((field) => (field.selected = []))
        play.perPlayAddOn = getDefaultPerPlayAddon(config)
        play.stake = getDefaultStake(config)
        removeEmptyPlaysExcept(playId)
      }

      const quickPickPlaysWithSettings = (config: Config, settings: QuickPickSettings) =>
        quickPickPlays(config, settings.quantity, (play) =>
          quickPickPlayWithSettings(lotteryId, config, play, settings),
        )

      const setPerPlayAddOn = (
        playId: string,
        perPlayAddOn: EditorPlay['perPlayAddOn'],
        config: Config,
      ) => {
        const play = getPlay(playId)
        if (!play) return
        play.perPlayAddOn = perPlayAddOn
        clearIgnoredFields(config, play)
      }

      const setQuickPickSettings = (settings: Partial<QuickPickSettings>) => {
        quickPickSettings.value = { ...quickPickSettings.value, ...settings }
      }

      const setSelectedNumbers = (playId: string, fieldIndex: number, selected: number[]) =>
        setFieldSelected(getPlay(playId), fieldIndex, selected)

      const setUp = (config: Config) => {
        setUpPlays(config)

        const { betType, numbers, stake } = quickPickSettings.value
        const options = getPerPlayAddOnConfig(config)?.options ?? []

        setQuickPickSettings({
          betType: betType && options.includes(betType) ? betType : null,
          numbers:
            numbers?.map((value, fieldIndex) =>
              isNumberSelectable(config, fieldIndex, value) ? value : null,
            ) ?? null,
          stake: stake && config.stakes.some(({ price }) => price === stake) ? stake : null,
        })
      }

      const setStake = (playId: string, stake: EditorPlay['stake']) => {
        const play = getPlay(playId)
        if (!play) return
        play.stake = stake
      }

      return {
        ...playsState,
        canDeleteAllPlays,
        clearPlay,
        getDisabledFieldNames,
        getIgnoredFieldNames,
        isPlayComplete,
        quickPickPlaysWithSettings,
        quickPickSettings,
        setPerPlayAddOn,
        setQuickPickSettings,
        setSelectedNumbers,
        setStake,
        setUp,
      }
    },
    {
      persist: {
        omit: ['expandedPlayId', 'pendingRemovals'],
        storage: editorStorage(),
      },
    },
  )

export const usePickLotteryStore = createLotteryStoreCache(createStore)
